import React, { useEffect, useState } from 'react';
import * as organizationsAPI from '../../../api/organizationsAPI';
import * as cloudinaryAPI from '../../../api/cloudinaryAPI';
import { Column, Row } from 'react-foundation';
import OpportunityButton from './opportunities/OpportunityButton';
import { CountrySelect } from '../shared/CountrySelect';
import { StateSelect } from '../shared/StateSelect';
import { CheckboxContainer } from '../shared/CheckboxContainer';
import MediaUploadButton from './opportunities/MediaUploadButton';
import { OrganizationAutocomplete } from '../shared/OrganizationAutocomplete';

/**
 * Edit Organization page
 * @param orgID orgID
 * @param organizations list of all organizations, passed from the backend
 */
const EditOrganization = ({ orgID, organizations }) => {
	const orgTemplate = {
		address_line_1: '',
		address_line_2: '',
		admin_user_id: '',
		affiliation: '',
		approved: '',
		audience_age_group: '',
		audience_gender: '',
		audience_size: '',
		charity: '',
		charity_number: '',
		city: '',
		country: '',
		created_at: '',
		description: '',
		facebook_followers: '',
		facebook_url: '',
		follows_count: '',
		id: '',
		images: [],
		instagram_followers: '',
		instagram_url: '',
		interest_areas: [],
		lat: '',
		legal_name: '',
		linkedin_followers: '',
		linkedin_url: '',
		lng: '',
		logo: '',
		operating_name: '',
		organization_email: '',
		organization_recommendation_for_users: [],
		organization_type: '',
		other_partners: '',
		partners: [],
		postal_code: '',
		searchable_text: '',
		sponsorship_url: '',
		state: '',
		tiktok_followers: '',
		tiktok_url: '',
		twitter_followers: '',
		twitter_url: '',
		updated_at: '',
		users_and_contacts: [],
		view_count: '',
		visitor_activity_email_send_date: '',
		website: '',
		youtube_followers: '',
		youtube_url: '',
	};

	const fieldDefaultsTemplate = {
		industries: [],
		interestAreas: [],
		presenceValidations: [],
		regions: [],
		organizationTypes: [],
	};

	const [orgData, setOrgData] = useState(orgTemplate);
	const [fieldDefaults, setFieldDefaults] = useState(fieldDefaultsTemplate);
	const [pageRefresh, setPageRefresh] = useState(false);
	const [logoName, setLogoName] = useState('');
	const [requiredFields, setRequiredFields] = useState([]);
	const emptyFieldMessage = 'Field cannot be left blank!';
	const emptySelectionMessage = 'Selection cannot be empty!';

	/**
	 * Does the following on page load
	 */
	useEffect(() => {
		fetchOrgDataAndFieldDefaults();
	}, []);

	/**
	 * Fetches organization data and field defaults
	 */
	const fetchOrgDataAndFieldDefaults = async () => {
		let response = await organizationsAPI.getOrganization(orgID);
		let organization = response.data;

		// Format the partners to have a name, that's legal_name -> This is used for displaying existing partners in checkboxes
		for (let partner of organization.partners) {
			partner.name = partner.legal_name;
		}

		setOrgData(organization);

		if (organization.state) {
			document.getElementById('state').value = organization.state;
		}
		if (organization.country) {
			document.getElementById('country').value = organization.country;
		}

		let fieldResponse = await organizationsAPI.getFieldAttributes();
		setFieldDefaults(fieldResponse.data);
		setRequiredFields(fieldResponse.data.presenceValidations);

		// For each required field, append an asterisk to the end of the header for it
		for (const requiredField of fieldResponse.data.presenceValidations) {
			let element = document.getElementById(requiredField + '-header');
			if (element) {
				element.textContent += ' *';
			}
		}

		// Set all checkboxes that should be active to be active
		for (let i = 0; i < organization.interest_areas.length; i++) {
			let currentId = organization.interest_areas[i].id;
			document.getElementById(
				'interest_areas' + currentId
			).checked = true;
		}

		for (let i = 0; i < organization.industries.length; i++) {
			let currentId = organization.industries[i].id;
			document.getElementById('industries' + currentId).checked = true;
		}
	};

	/**
	 * Redirects the user to the show organization page
	 */
	const redirectToOrganization = () => {
		location.assign(`/organizations/${orgID}`);
	};

	/**
	 * Handles submission of the edit page
	 */
	const handleSubmit = async () => {
		let allRequiredFieldsFilled = areRequiredFieldsFilled();

		if (allRequiredFieldsFilled) {
			try {
				let response = await organizationsAPI.putOrganization(
					orgID,
					orgData
				);
				if (response.status === 200) {
					redirectToOrganization();
				} else if (response.status === 422) {
					// If there's an error with the update request
				}
			} catch (e) {
				console.error(e);
			}
		}
	};

	const areRequiredFieldsFilled = () => {
		let allRequiredFieldsFilled = true;
		let hasScrolledAlready = false;

		for (const requiredField of requiredFields) {
			if (
				orgData[requiredField] === '' ||
				orgData[requiredField].length === 0
			) {
				allRequiredFieldsFilled = false;
				document.getElementById(
					requiredField + '-empty-label'
				).style.display = 'inline-block';
				if (!hasScrolledAlready) {
					document
						.getElementById(requiredField + '-header')
						.scrollIntoView({ behavior: 'smooth' });
					hasScrolledAlready = true;
				}
			} else {
				if (requiredField !== 'organization_type') {
					document.getElementById(
						requiredField + '-empty-label'
					).style.display = 'none';
				}
			}
		}

		return allRequiredFieldsFilled;
	};

	/**
	 * Handles any single input field data change, and not arrays
	 * @param event the event where the change occurred
	 */
	const handleOrgDataChange = (event) => {
		if (event.target.name === 'charity') {
			orgData['charity'] = event.target.checked;
		} else {
			orgData[event.target.name] = event.target.value;
		}
		setOrgData(orgData);

		if (requiredFields.includes(event.target.name)) {
			if (event.target.value === '') {
				document.getElementById(
					event.target.name + '-empty-label'
				).style.display = 'inline-block';
			} else {
				document.getElementById(
					event.target.name + '-empty-label'
				).style.display = 'none';
			}
		}
	};

	/**
	 * Handles clicking of a checkbox under a tag section.
	 * @param event the event of the checkbox clicked
	 * @param type the type of tag, either interest_area or industry
	 */
	const handleCheckboxes = (event, type) => {
		let typeToUse = type;
		if (typeToUse === 'interest_areas') {
			typeToUse = 'interestAreas';
		}
		// If the target has now become checked, then add the data to our orgData object
		if (event.target.checked) {
			let currentType;
			for (let i = 0; i < fieldDefaults[typeToUse].length; i++) {
				if (
					String(event.target.name) ===
					String(fieldDefaults[typeToUse][i].id)
				) {
					currentType = fieldDefaults[typeToUse][i];
					break;
				}
			}

			if (currentType) {
				orgData[type].push({
					id: parseInt(event.target.name),
					name: event.target.value,
				});
				setOrgData(orgData);
			}
		} else {
			// If the target has just become unchecked, then remove it from our stored orgData object
			orgData[type].forEach((sponsorableOpportunity, index) => {
				if (
					String(sponsorableOpportunity.id) ===
					String(event.target.name)
				) {
					orgData[type].splice(index, 1);
					setOrgData(orgData);
				}
			});
		}
	};

	/**
	 * Checks if the current checkbox should start checked
	 * @param name the name of the checkbox
	 * @return {boolean} true if it should be checked, false if not
	 */
	const isTagDefaultChecked = (name) => {
		for (let i = 0; i < orgData.interest_areas.length; i++) {
			if (String(orgData.interest_areas[i].id) === String(name)) {
				return true;
			}
		}
		return false;
	};

	/**
	 * Redirects the user to change their organization type
	 */
	const redirectToChangeOrganizationType = () => {
		location.assign(`/organizations/${orgID}/edit_type`);
	};

	/**
	 * Redirect the user to the attach organization page
	 */
	const redirectToAttachOrganization = () => {
		location.assign(`/attach`);
	};

	const handleClearingMedia = () => {
		orgData['logo'] = '';
		setOrgData(orgData);
		setPageRefresh(!pageRefresh);
	};

	const handleMediaSubmission = (name, link) => {
		orgData['logo'] = link;
		setOrgData(orgData);
	};

	return (
		<div className="edit-organization">
			<Row>
				<Column small={12}>
					<div className="title-div top-10px-margin">
						<p className="body-font">
							You are attached to this organization. If you want
							to change, click{' '}
							<a onClick={redirectToAttachOrganization}>here</a>
						</p>
						<h1>
							Edit {orgData.legal_name} (
							{orgData.organization_type})
						</h1>
						<div
							className="primary-button float-right top-10px-margin"
							onClick={redirectToChangeOrganizationType}
						>
							<p>Change Organization Type</p>
						</div>
					</div>
				</Column>
			</Row>

			<Row>
				<Column small={12}>
					<h6 className="header-font margin-top-1">Logo image</h6>
					{orgData.logo?.url ? (
						<img
							className="logo-img"
							src={orgData.logo?.url}
							alt={`${orgData?.legal_name} Logo`}
						/>
					) : (
						<></>
					)}
					<div className="header-image-container">
						<MediaUploadButton
							name="header_media"
							handleMediaSubmission={handleMediaSubmission}
							text="Update logo image"
							icon={true}
							className="header-media-button float-left"
							setMediaFileName={setLogoName}
							handleClearingMedia={handleClearingMedia}
						/>
					</div>
					{orgData.logo?.url ? (
						<p className="body-font image-name">
							Current logo:{' '}
							{cloudinaryAPI.formatCloudinaryURLToDisplayToUser(
								orgData.logo?.url
							)}
						</p>
					) : (
						''
					)}
				</Column>
			</Row>

			<Row>
				<Column small={12}>
					<h3 className="top-10px-margin">Organization</h3>
				</Column>
			</Row>

			<Row>
				<Column small={12} medium={6}>
					<h6 className="header-font" id="legal_name-header">
						Legal Name
					</h6>
					<input
						type="text"
						className="body-font-important"
						placeholder="Enter your organization legal name (for content creators, enter your legal name)"
						defaultValue={orgData.legal_name}
						onChange={(e) => handleOrgDataChange(e)}
						name="legal_name"
						id="legal_name"
					/>
					<label
						className="empty-field-label"
						id="legal_name-empty-label"
					>
						{emptyFieldMessage}
					</label>
				</Column>
				<Column small={12} medium={6}>
					<h6 className="header-font">Operating Name</h6>
					<input
						type="text"
						className="body-font-important"
						defaultValue={orgData.operating_name}
						onChange={(e) => handleOrgDataChange(e)}
						name="operating_name"
					/>
				</Column>
			</Row>

			<Row>
				<Column small={12}>
					<h6 className="header-font" id="description-header">
						Description
					</h6>
					<textarea
						className="body-font-important"
						placeholder="Tell us about your organization"
						defaultValue={orgData.description}
						onChange={(e) => handleOrgDataChange(e)}
						name="description"
						id="description"
					/>
					<label
						className="empty-field-label"
						id="description-empty-label"
					>
						{emptyFieldMessage}
					</label>
				</Column>
			</Row>

			<Row>
				<Column small={12}>
					<h3 className="top-10px-margin">Location</h3>
				</Column>
			</Row>

			<Row>
				<Column small={12} medium={6}>
					<h6 className="header-font">Street Address</h6>
					<input
						type="text"
						className="body-font-important"
						defaultValue={orgData.address_line_1}
						onChange={(e) => handleOrgDataChange(e)}
						name="address_line_1"
					/>
				</Column>
				<Column small={12} medium={6}>
					<h6 className="header-font">City</h6>
					<input
						type="text"
						className="body-font-important"
						defaultValue={orgData.city}
						onChange={(e) => handleOrgDataChange(e)}
						name="city"
					/>
				</Column>
			</Row>

			<Row>
				<Column
					small={8}
					medium={4}
					className="half-width-columns-on-mobile"
				>
					<h6 className="header-font">Postal Code / Zip Code</h6>
					<input
						type="text"
						className="body-font-important"
						defaultValue={orgData.postal_code}
						onChange={(e) => handleOrgDataChange(e)}
						name="postal_code"
					/>
				</Column>
				<Column
					small={8}
					medium={4}
					className="half-width-columns-on-mobile"
				>
					<h6 className="header-font">Province / State / Region</h6>
					<StateSelect
						defaultValue={orgData.state}
						name="state"
						handleChange={handleOrgDataChange}
						id="state"
					/>
				</Column>
				<Column
					small={8}
					medium={4}
					className="half-width-columns-on-mobile"
				>
					<h6 className="header-font" id="country-header">
						Country
					</h6>
					<CountrySelect
						defaultValue={orgData.country}
						name="country"
						handleChange={handleOrgDataChange}
						id="country"
					/>
					<label
						className="empty-field-label"
						id="country-empty-label"
					>
						{emptySelectionMessage}
					</label>
				</Column>
			</Row>

			<Row>
				<Column small={12}>
					<h3 className="top-10px-margin">Organization Details</h3>
				</Column>
			</Row>

			<Row>
				<Column small={12} medium={6}>
					<h6 className="header-font">Do you have a website?</h6>
					<input
						type="text"
						className="body-font-important"
						placeholder="Please enter your URL e.g. www.mywebsite.com"
						defaultValue={orgData.website}
						onChange={(e) => handleOrgDataChange(e)}
						name="website"
					/>
				</Column>
				<Column small={12} medium={6}>
					<h6 className="header-font">
						Do you have a website link to an online application or
						sponsorship page?
					</h6>
					<input
						type="text"
						className="body-font-important"
						placeholder="Please enter your URL e.g. www.mywebsite.com/sponsorshippage"
						defaultValue={orgData.sponsorship_url}
						onChange={(e) => handleOrgDataChange(e)}
						name="sponsorship_url"
					/>
				</Column>
			</Row>

			<Row>
				<Column small={12} medium={6}>
					<h6 className="header-font">Organization Email</h6>
					<input
						type="text"
						className="body-font-important"
						defaultValue={orgData.organization_email}
						onChange={(e) => handleOrgDataChange(e)}
						name="organization_email"
					/>
				</Column>
			</Row>

			<Row>
				<Column small={12}>
					<h3 className="top-10px-margin">Interest Areas</h3>
				</Column>
			</Row>

			<Row>
				<Column small={12}>
					<h6 className="header-font" id="interest_areas-header">
						How would you classify your sponsorable opportunities?
					</h6>
					<CheckboxContainer
						orgID={orgID}
						handleCheckboxes={handleCheckboxes}
						data={fieldDefaults.interestAreas}
						isCheckboxDefaultChecked={isTagDefaultChecked}
						tagPrefix="interest_areas"
						name="interest_areas"
						id="interest_areas"
					/>
					<label
						className="empty-field-label"
						id="interest_areas-empty-label"
					>
						{emptySelectionMessage}
					</label>
				</Column>
			</Row>

			<Row>
				<Column small={12}>
					<h3 className="top-10px-margin">Social Media Presence</h3>
				</Column>
			</Row>

			<Row>
				<Column small={12} medium={6}>
					<Row>
						<Column
							small={16}
							medium={8}
							className="half-width-columns-on-mobile"
						>
							<h6 className="header-font">Facebook Page</h6>
							<input
								type="text"
								className="body-font-important"
								placeholder="Please enter your URL e.g. www.facebook.com/mypage"
								defaultValue={orgData.facebook_url}
								onChange={(e) => handleOrgDataChange(e)}
								name="facebook_url"
							/>
						</Column>
						<Column
							small={8}
							medium={4}
							className="half-width-columns-on-mobile"
						>
							<h6 className="header-font">Followers</h6>
							<input
								type="number"
								className="body-font-important"
								placeholder="0"
								defaultValue={orgData.facebook_followers}
								onChange={(e) => handleOrgDataChange(e)}
								name="facebook_followers"
							/>
						</Column>
					</Row>

					<Row>
						<Column
							small={16}
							medium={8}
							className="half-width-columns-on-mobile"
						>
							<h6 className="header-font">Instagram Profile</h6>
							<input
								type="text"
								className="body-font-important"
								placeholder="Please enter your URL e.g. www.instagram.com/mypage"
								defaultValue={orgData.instagram_url}
								onChange={(e) => handleOrgDataChange(e)}
								name="instagram_url"
							/>
						</Column>
						<Column
							small={8}
							medium={4}
							className="half-width-columns-on-mobile"
						>
							<h6 className="header-font">Followers</h6>
							<input
								type="number"
								className="body-font-important"
								placeholder="0"
								defaultValue={orgData.instagram_followers}
								onChange={(e) => handleOrgDataChange(e)}
								name="instagram_followers"
							/>
						</Column>
					</Row>

					<Row>
						<Column
							small={16}
							medium={8}
							className="half-width-columns-on-mobile"
						>
							<h6 className="header-font">Twitter Page</h6>
							<input
								type="text"
								className="body-font-important"
								placeholder="Please enter your URL e.g. www.twitter.com/mypage"
								defaultValue={orgData.twitter_url}
								onChange={(e) => handleOrgDataChange(e)}
								name="twitter_url"
							/>
						</Column>
						<Column
							small={8}
							medium={4}
							className="half-width-columns-on-mobile"
						>
							<h6 className="header-font">Followers</h6>
							<input
								type="number"
								className="body-font-important"
								placeholder="0"
								defaultValue={orgData.twitter_followers}
								onChange={(e) => handleOrgDataChange(e)}
								name="twitter_followers"
							/>
						</Column>
					</Row>
				</Column>
				<Column small={12} medium={6}>
					<Row>
						<Column
							small={16}
							medium={8}
							className="half-width-columns-on-mobile"
						>
							<h6 className="header-font">Tiktok Account</h6>
							<input
								type="text"
								className="body-font-important"
								placeholder="Please enter your URL e.g. www.tiktok.com/mypage"
								defaultValue={orgData.tiktok_url}
								onChange={(e) => handleOrgDataChange(e)}
								name="tiktok_url"
							/>
						</Column>
						<Column
							small={8}
							medium={4}
							className="half-width-columns-on-mobile"
						>
							<h6 className="header-font">Followers</h6>
							<input
								type="number"
								className="body-font-important"
								placeholder="0"
								defaultValue={orgData.tiktok_followers}
								onChange={(e) => handleOrgDataChange(e)}
								name="tiktok_followers"
							/>
						</Column>
					</Row>

					<Row>
						<Column
							small={16}
							medium={8}
							className="half-width-columns-on-mobile"
						>
							<h6 className="header-font">Youtube Channel</h6>
							<input
								type="text"
								className="body-font-important"
								placeholder="Please enter your URL e.g. www.youtube.com/mypage"
								defaultValue={orgData.youtube_url}
								onChange={(e) => handleOrgDataChange(e)}
								name="youtube_url"
							/>
						</Column>
						<Column
							small={8}
							medium={4}
							className="half-width-columns-on-mobile"
						>
							<h6 className="header-font">Followers</h6>
							<input
								type="number"
								className="body-font-important"
								placeholder="0"
								defaultValue={orgData.youtube_followers}
								onChange={(e) => handleOrgDataChange(e)}
								name="youtube_followers"
							/>
						</Column>
					</Row>

					<Row>
						<Column
							small={16}
							medium={8}
							className="half-width-columns-on-mobile"
						>
							<h6 className="header-font">Linkedin account</h6>
							<input
								type="text"
								className="body-font-important"
								placeholder="Please enter your URL e.g. www.linkedin.com/mypage"
								defaultValue={orgData.linkedin_url}
								onChange={(e) => handleOrgDataChange(e)}
								name="linkedin_url"
							/>
						</Column>
						<Column
							small={8}
							medium={4}
							className="half-width-columns-on-mobile"
						>
							<h6 className="header-font">Followers</h6>
							<input
								type="number"
								className="body-font-important"
								placeholder="0"
								defaultValue={orgData.linkedin_followers}
								onChange={(e) => handleOrgDataChange(e)}
								name="linkedin_followers"
							/>
						</Column>
					</Row>
				</Column>
			</Row>

			{orgData.organization_type === 'Property' ? (
				<Row>
					<Column small={12}>
						<input
							type="checkbox"
							name="charity"
							onChange={(event) => handleOrgDataChange(event)}
							defaultChecked={orgData?.property_info?.charity}
						/>
						<label className="header-font">
							Are you a registered charity?
						</label>
						<h6 className="header-font">Charity Number</h6>
						<input
							type="number"
							className="body-font-important"
							defaultValue={
								orgData?.property_info?.charity_number
							}
							name="charity_number"
							onChange={(event) => handleOrgDataChange(event)}
						/>
					</Column>
				</Row>
			) : (
				''
			)}

			<Row>
				<Column small={12}>
					<h3 className="top-10px-margin">Industries</h3>
					<h6 className="header-font">
						In what industry are you classified?
					</h6>
					<CheckboxContainer
						orgID={orgID}
						handleCheckboxes={handleCheckboxes}
						data={fieldDefaults.industries}
						isCheckboxDefaultChecked={isTagDefaultChecked}
						tagPrefix="industries"
					/>
				</Column>
			</Row>

			<Row>
				<Column small={12}>
					<h3 className="top-10px-margin">Partners</h3>
					<h6 className="header-font">
						Sponsorship partnerships we are proud of
					</h6>
					<OrganizationAutocomplete
						organizations={organizations}
						data={orgData}
						setData={setOrgData}
						type="EditOrganization"
					/>
					<h6 className="header-font top-10px-margin">
						Other partners
					</h6>
					<input
						type="text"
						className="body-font-important"
						placeholder="Enter your sponsorship partners here if there is no existing match above, seperated by commas"
						defaultValue={orgData.other_partners}
						onChange={(event) => handleOrgDataChange(event)}
						name="other_partners"
					/>
				</Column>
			</Row>

			<Row>
				<Column small={12}>
					<OpportunityButton
						text="Cancel"
						className="secondary-button float-left"
						handleSubmit={redirectToOrganization}
						isBackLink={false}
						leftButtonText="Cancel"
						rightButtonText="Exit"
						headerText="You have unsaved content. Would you like to exit and lose your work?"
					/>
					<OpportunityButton
						text="Save"
						className="primary-button float-right push-down-on-mobile"
						handleSubmit={handleSubmit}
						isBackLink={false}
						leftButtonText="Cancel"
						rightButtonText="Continue"
						headerText="Your changes will be immediately active once published."
					/>
				</Column>
			</Row>
		</div>
	);
};

export default EditOrganization;
